import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  User,
  ClassificationGroup,
  Classification,
  ClassificationLevel,
} from '@core/generated/gql-types';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { userActions } from '@core/user/user.action';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useTenantId } from '@/contexts/TenantContext';
import {
  actionChipClassName,
  actionListboxButtonMdClassName,
  actionListboxOptionActiveClassName,
  actionPrimaryButtonClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';

interface ClassificationLevelEditorProps {
  worker: User;
  isEditable?: boolean;
  isSaving?: boolean;
}

export interface ClassificationLevelEditorRef {
  saveChanges: () => Promise<boolean>;
  hasChanges: () => boolean;
}

interface LocalLevel {
  id: string;
  classificationGroup: ClassificationGroup;
  classification: Classification;
  classificationLevel?: ClassificationLevel; // undefined if incomplete (has classification but no level)
  isNew?: boolean; // Track if it's a new addition (not yet saved)
  isIncomplete?: boolean; // True if this is a classification without a level assigned
}

interface ModalState {
  classificationGroup: ClassificationGroup | undefined;
  classification: Classification | undefined;
  classificationLevel: ClassificationLevel | undefined;
}

const ClassificationLevelEditor = forwardRef<
  ClassificationLevelEditorRef,
  ClassificationLevelEditorProps
>(({ worker, isEditable = true, isSaving = false }, ref) => {
  const tenantId = useTenantId();
  const {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
    fetchClassificationGroups,
    isLoading,
  } = useClassificationStore();
  const { fetchWorkerById, updateWorker } = useWorkerStore();
  const { getTerm } = useTemplateStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLevelId, setEditingLevelId] = useState<string | null>(null);

  // Local state representing current levels (may differ from worker.classificationLevelIds)
  const [localLevels, setLocalLevels] = useState<LocalLevel[]>([]);
  const [originalLevelIds, setOriginalLevelIds] = useState<Set<string>>(
    new Set(),
  );
  // Track removed incomplete classification IDs so they don't get re-added by useEffect
  // Using ref instead of state to avoid triggering useEffect and causing flicker
  const removedIncompleteIdsRef = useRef<Set<string>>(new Set());

  // Modal form state
  const [modalState, setModalState] = useState<ModalState>({
    classificationGroup: undefined,
    classification: undefined,
    classificationLevel: undefined,
  });

  // Initialize local levels from worker data
  useEffect(() => {
    if (classificationGroups.length === 0) return;

    const levels: LocalLevel[] = [];
    const ids = new Set<string>();

    // Track which classificationIds have levels assigned
    const classificationIdsWithLevels = new Set<string>();

    // First, add all existing classification levels from classificationLevelIds
    worker.classificationLevelIds?.forEach((levelId) => {
      const level = getClassificationLevelById(levelId);
      if (!level) return;

      const classification = getClassificationById(level.classificationId!);
      const group = classificationGroups.find((g) =>
        g.classifications?.some((c) => c?.id === level.classificationId),
      );

      if (group && classification) {
        levels.push({
          id: level.id!,
          classificationGroup: group,
          classification,
          classificationLevel: level,
          isNew: false,
          isIncomplete: false,
        });
        ids.add(level.id!);
        classificationIdsWithLevels.add(level.classificationId!);
      }
    });

    // Then, add incomplete rows for classificationIds without levels
    worker.classificationIds?.forEach((classificationId) => {
      // Skip if this classification already has a level
      if (classificationIdsWithLevels.has(classificationId)) return;

      // Skip if this incomplete classification was removed by the user
      if (removedIncompleteIdsRef.current.has(classificationId)) return;

      const classification = classificationGroups
        .flatMap((g) => g.classifications)
        .find((c) => c?.id === classificationId);

      const group = classificationGroups.find((g) =>
        g.classifications?.some((c) => c?.id === classificationId),
      );

      if (group && classification) {
        levels.push({
          id: `incomplete-${classificationId}`,
          classificationGroup: group,
          classification,
          classificationLevel: undefined,
          isNew: false,
          isIncomplete: true,
        });
      }
    });

    setLocalLevels(levels);
    setOriginalLevelIds(ids);
  }, [
    worker.classificationLevelIds,
    worker.classificationIds,
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
  ]);

  useEffect(() => {
    if (tenantId) {
      fetchClassificationGroups(tenantId);
    }
  }, [tenantId, fetchClassificationGroups]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    saveChanges: async () => {
      try {
        // Filter out incomplete levels (no classificationLevel assigned)
        const completeLevels = localLevels.filter((l) => l.classificationLevel);

        // Find levels to remove (in original but not in current complete levels)
        const currentIds = new Set(
          completeLevels.map((l) => l.classificationLevel!.id),
        );
        // Find levels to remove (in original but not in current complete levels)
        const toRemove = Array.from(originalLevelIds).filter(
          (id) => !currentIds.has(id),
        );

        // Process removals
        for (const levelId of toRemove) {
          await userActions.removeClassificationLevelFromUser(
            worker.id,
            levelId,
          );
        }

        // Find levels to add (in current but not in original, or marked as new, or was incomplete but now complete)
        const toAdd = completeLevels.filter(
          (l) =>
            l.isNew ||
            l.isIncomplete || // Was incomplete, now has a level
            !originalLevelIds.has(l.classificationLevel!.id!),
        );

        // Process additions
        for (const level of toAdd) {
          await userActions.assignClassificationLevelToUser(
            worker.id,
            level.classificationLevel!.id!,
          );
        }

        // If any incomplete classifications were removed, update the user's classificationIds
        if (removedIncompleteIdsRef.current.size > 0) {
          const updatedClassificationIds = (
            worker.classificationIds || []
          ).filter((id) => !removedIncompleteIdsRef.current.has(id));

          await updateWorker(worker.id, {
            classificationIds: updatedClassificationIds,
          });
        }

        // Reset removed incomplete IDs after successful save
        removedIncompleteIdsRef.current = new Set();

        await fetchWorkerById(worker.id);
        toast.success(
          `${getTerm(TemplateTerm.WORKER, false)} updated successfully`,
        );
        return true;
      } catch (error) {
        console.error('Failed to save classification levels:', error);
        toast.error('Failed to save classification levels');
        return false;
      }
    },
    hasChanges: () => {
      // Check if any incomplete classifications were removed
      if (removedIncompleteIdsRef.current.size > 0) return true;

      const completeLevels = localLevels.filter((l) => l.classificationLevel);
      const currentIds = new Set(
        completeLevels.map((l) => l.classificationLevel!.id),
      );

      // Check if any original level was removed
      for (const id of originalLevelIds) {
        if (!currentIds.has(id)) return true;
      }

      // Check if any new level was added or incomplete level was completed
      for (const level of completeLevels) {
        if (
          level.isNew ||
          level.isIncomplete || // Was incomplete, now has a level
          !originalLevelIds.has(level.classificationLevel!.id!)
        ) {
          return true;
        }
      }

      // Check if any level was removed (fewer complete levels than original)
      if (completeLevels.length !== originalLevelIds.size) return true;

      return false;
    },
  }));

  const sortedClassificationGroups = [...classificationGroups].sort((a, b) =>
    (a?.name ?? '').localeCompare(b?.name ?? ''),
  );

  // Get available classifications for selected group (A–Z)
  const availableClassifications = [
    ...(modalState.classificationGroup?.classifications?.filter(Boolean) || []),
  ].sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));

  // Get available levels for selected classification
  const availableLevels =
    modalState.classification?.classificationLevels?.filter(Boolean) || [];

  const openModal = () => {
    setEditingLevelId(null);
    setModalState({
      classificationGroup: undefined,
      classification: undefined,
      classificationLevel: undefined,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (localLevel: LocalLevel) => {
    setEditingLevelId(localLevel.id);
    setModalState({
      classificationGroup: localLevel.classificationGroup,
      classification: localLevel.classification,
      classificationLevel: localLevel.classificationLevel || undefined,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLevelId(null);
    setModalState({
      classificationGroup: undefined,
      classification: undefined,
      classificationLevel: undefined,
    });
  };

  const handleGroupChange = (group: ClassificationGroup) => {
    setModalState({
      classificationGroup: group,
      classification: undefined,
      classificationLevel: undefined,
    });
  };

  const handleClassificationChange = (classification: Classification) => {
    setModalState({
      ...modalState,
      classification,
      classificationLevel: undefined,
    });
  };

  const handleLevelChange = (level: ClassificationLevel) => {
    setModalState({
      ...modalState,
      classificationLevel: level,
    });
  };

  const handleConfirm = () => {
    if (
      !modalState.classificationGroup ||
      !modalState.classification ||
      !modalState.classificationLevel
    ) {
      toast.error('Please select all fields');
      return;
    }

    const newLevelId = modalState.classificationLevel.id!;

    // Check if level is already in local levels (excluding the one being edited)
    const alreadyExists = localLevels.some(
      (l) =>
        l.classificationLevel?.id === newLevelId && l.id !== editingLevelId,
    );

    if (alreadyExists) {
      toast.error('This classification level is already assigned');
      return;
    }

    if (editingLevelId) {
      // Edit existing (or complete an incomplete row)
      setLocalLevels(
        localLevels.map((l) => {
          if (l.id !== editingLevelId) return l;

          const wasIncomplete = l.isIncomplete;
          const levelChanged = l.classificationLevel?.id !== newLevelId;

          return {
            ...l,
            classificationGroup: modalState.classificationGroup!,
            classification: modalState.classification!,
            classificationLevel: modalState.classificationLevel!,
            // Mark as new if it was incomplete or the level changed
            isNew: l.isNew || wasIncomplete || levelChanged,
            isIncomplete: false, // No longer incomplete
          };
        }),
      );
    } else {
      // Add new
      const newLocal: LocalLevel = {
        id: `temp-${Date.now()}`,
        classificationGroup: modalState.classificationGroup,
        classification: modalState.classification,
        classificationLevel: modalState.classificationLevel,
        isNew: true,
        isIncomplete: false,
      };
      setLocalLevels([...localLevels, newLocal]);
    }

    closeModal();
  };

  const handleRemove = (levelId: string) => {
    // Find the level being removed
    const levelToRemove = localLevels.find((l) => l.id === levelId);

    // If it's an incomplete row, track it so it doesn't get re-added by useEffect
    if (levelToRemove?.isIncomplete && levelToRemove.classification.id) {
      removedIncompleteIdsRef.current = new Set([
        ...removedIncompleteIdsRef.current,
        levelToRemove.classification.id!,
      ]);
    }

    setLocalLevels(localLevels.filter((l) => l.id !== levelId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          Classification Levels
        </h4>
        {isEditable && (
          <button
            type="button"
            onClick={openModal}
            disabled={isSaving}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionChipClassName}`}
          >
            <PlusIcon className="h-4 w-4" />
            Add
          </button>
        )}
      </div>

      {/* Classification Levels List */}
      {localLevels.length > 0 ? (
        <div className="grid grid-cols-4 gap-4">
          {localLevels.map((localLevel) => (
            <div
              key={localLevel.id}
              className={`flex flex-col justify-between rounded-lg border p-3 ${
                localLevel.isIncomplete
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {localLevel.classificationLevel?.name || (
                      <span className="text-amber-600 italic">
                        Level not assigned
                      </span>
                    )}
                  </span>
                  {localLevel.isIncomplete && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                      Incomplete
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {localLevel.classificationGroup.name} •{' '}
                  {localLevel.classification.name}
                </div>
              </div>
              {isEditable && (
                <div className="mt-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => openEditModal(localLevel)}
                    disabled={isSaving}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                    title={localLevel.isIncomplete ? 'Assign Level' : 'Edit'}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(localLevel.id)}
                    disabled={isSaving}
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Remove"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No classification levels assigned
        </p>
      )}

      {/* Add/Edit Classification Level Modal */}
      <Transition appear show={isModalOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {editingLevelId
                      ? 'Edit Classification Level'
                      : 'Add Classification Level'}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    {/* Classification Group (Industry) */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Industry
                      </label>
                      <Listbox
                        value={modalState.classificationGroup}
                        onChange={handleGroupChange}
                        disabled={isLoading}
                      >
                        <div className="relative">
                          <Listbox.Button
                            className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                          >
                            <span
                              className={`block truncate ${!modalState.classificationGroup ? 'text-gray-400' : ''}`}
                            >
                              {modalState.classificationGroup?.name ||
                                'Select industry'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                              {sortedClassificationGroups.map((group) => (
                                <Listbox.Option
                                  key={group.id}
                                  value={group}
                                  className={({ active }) =>
                                    `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                      active
                                        ? actionListboxOptionActiveClassName
                                        : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                      >
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                        >
                                          <CheckIcon className="h-5 w-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>

                    {/* Classification (Skill Set) */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Skill Set
                      </label>
                      <Listbox
                        value={modalState.classification}
                        onChange={handleClassificationChange}
                        disabled={!modalState.classificationGroup}
                      >
                        <div className="relative">
                          <Listbox.Button
                            className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                          >
                            <span
                              className={`block truncate ${!modalState.classification ? 'text-gray-400' : ''}`}
                            >
                              {modalState.classification?.name ||
                                'Select skill set'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                              {availableClassifications.map(
                                (classification) => (
                                  <Listbox.Option
                                    key={classification?.id}
                                    value={classification}
                                    className={({ active }) =>
                                      `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                        active
                                          ? actionListboxOptionActiveClassName
                                          : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                        >
                                          {classification?.name}
                                        </span>
                                        {selected && (
                                          <span
                                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                          >
                                            <CheckIcon className="h-5 w-5" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ),
                              )}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>

                    {/* Classification Level */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Level
                      </label>
                      <Listbox
                        value={modalState.classificationLevel}
                        onChange={handleLevelChange}
                        disabled={!modalState.classification}
                      >
                        <div className="relative">
                          <Listbox.Button
                            className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
                          >
                            <span
                              className={`block truncate ${!modalState.classificationLevel ? 'text-gray-400' : ''}`}
                            >
                              {modalState.classificationLevel?.name ||
                                'Select level'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                              {availableLevels.map((level) => (
                                <Listbox.Option
                                  key={level?.id}
                                  value={level}
                                  className={({ active }) =>
                                    `relative cursor-default py-2 pr-4 pl-10 select-none ${
                                      active
                                        ? actionListboxOptionActiveClassName
                                        : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                      >
                                        {level?.name}
                                      </span>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                        >
                                          <CheckIcon className="h-5 w-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${actionPrimaryButtonClassName}`}
                      onClick={handleConfirm}
                      disabled={!modalState.classificationLevel}
                    >
                      {editingLevelId ? 'Update' : 'Add'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
});

ClassificationLevelEditor.displayName = 'ClassificationLevelEditor';

export default ClassificationLevelEditor;
